import { Box, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { uniqueBy } from "utils/globalsUtils";
import {
  getAttrArray,
  getAttributes,
  getBioPrefRelations,
  getIntroAttrArray,
} from "utils/attributesUtil";
import { Introduction } from "models/introduction/Introduction";
import { questionnarieListDo } from "utils/questionsUtils";
import colors from "assets/theme/base/colors";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { getProfileResponses } from "utils/enduserUtils";
import { ProfileQResponse } from "models/profile/ProfileResponse";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { ResponseCell } from "./cell";
import { useMaterialUIController } from "context";
import { PermissionType } from "models/BO/BoRole";
import { Attribute, MatchType } from "models/profile/Attribute";
import { BioPrefRelation } from "models/batch/BioPrefRelation";
import { PageNumber } from "../../../../accessNumbers";

export const AttributesTable = ({ request }: { request: Introduction }) => {
  const pageNumber = PageNumber.ViewIntroduction;
  const [controller, dispatch] = useMaterialUIController();
  const { currentRole } = controller;
  const [attributes, setAttributes] = useState([] as Attribute[]);
  const [relations, setRelations] = useState([] as BioPrefRelation[]);
  const [questionnaires, setQuestionnaires] = useState([] as any);
  const [initiatorProfileResponses, setInitiatorProfileResponses] = useState(
    [] as ProfileQResponse[]
  );
  const [responderProfileResponses, setResponderProfileResponses] = useState(
    [] as ProfileQResponse[]
  );

  useEffect(() => {
    Promise.all([getAttributes(true), getBioPrefRelations()]).then(([attrs, relations]) => {
      setAttributes(attrs);
      setRelations(relations);
    });

    
      questionnarieListDo().then((questionnaires) => {
        setQuestionnaires(questionnaires);
      });

    
      getProfileResponses(request.initiatorProfileId).then((responses) => {
        setInitiatorProfileResponses(responses);
      });
      getProfileResponses(request.responderProfileId).then((responses) => {
        setResponderProfileResponses(responses);
      });
  }, []);

  const initiatorName = request.initiatorUser?.firstName;
  const responderName = request.responderUser?.firstName;

  const tableCellCss = (match:MatchType) => {
    switch (match) {
      case MatchType.NoMatch:
        return "unmatch";
      case MatchType.Partial:
        return "partial";
      case MatchType.Exact:
        return "match";
      default:
        return "";
    }
  };

  const addResponses = (attrs) => {
    ;
    if (initiatorProfileResponses.length == 0 || responderProfileResponses.length == 0) {
      return uniqueBy(attrs, "bioName");
    }

    for (const response of initiatorProfileResponses) {
      const initiatorBioAttr = attrs.find((a) => a?.name == response.profileAttr);
      const initiatorPrefAttr = attrs.find((a) => a?.prefAttribute?.name == response.profileAttr);

      if (initiatorBioAttr) {
        initiatorBioAttr.reportedInititorBio = response.reported;
      } else if (initiatorPrefAttr) {
        initiatorPrefAttr.reportedInititorPref = response.reported;
      } 
      if (!response.profileAttr && response.batchId != "12") {
        const responderResponse = responderProfileResponses.find(
          (r) => r.batchContentId == response.batchContentId
        );
        const initiatorBio = response.response.join
          ? response.response.join(", ")
          : response.response;
        const responderBio = responderResponse?.response.join
          ? responderResponse?.response.join(", ")
          : responderResponse?.response;
        attrs.push({
          id: Number(response.batchContentId) + 99999,
          bioName: response.question,
          initiatorBio,
          responderBio,
          initiatorMatch: initiatorBio == responderBio,
          responderMatch: responderBio == initiatorBio,

          ...response,
        });
      }
    }
    for (const response of responderProfileResponses) {
      const responderBioAttr = attrs.find((a) => a?.name == response.profileAttr);
      const responderPrefAttr = attrs.find((a) => a?.prefAttribute?.name == response.profileAttr);
      if (responderBioAttr) {
        responderBioAttr.reportedResponderBio = response.reported;
      }
      if (responderPrefAttr) {
        responderPrefAttr.reportedResponderPref = response.reported;
      }

    }
    return uniqueBy(attrs, "bioName");
  };

  const getDb = (p:GridRenderCellParams) => {
    const row = p.row as any;
    if (row.isDealBreaker) {
      if (row.initiatorMatch == MatchType.NoMatch || row.responderMatch == MatchType.NoMatch) {
        return "❌"
      }
      if ([MatchType.Partial, MatchType.Exact].some((m) => m == row.initiatorMatch || m == row.responderMatch)) {
        return "✅"
      }
    }
    return "";
  }

  return (
    <Grid>
      <Grid
        justifyContent="space-evenly"
        display="flex"
        flexDirection="column"
        sx={{ width: "100%" }}
        style={{ zIndex: 2, position: "relative" }}
      >
        <Box
          sx={{
            "& .unmatch": {
              color: colors.error.main,
            },
            "& .match": {
              color: colors.success.main,
            },
            "& .partial": {
              color: colors.secondary.main,
            },
          }}
        >
          <DataGridPro
            rows={addResponses(getIntroAttrArray(attributes, relations, request, false))}
            getTreeDataPath={(row) => [
              questionnaires.find((q) => q.id == row.batchId)?.display_name || "Onboarding",
              row.bioName,
            ]}
            treeData
            columns={[
              {
                field: "DB",
                headerName: "DB",
                renderCell: getDb,
                width: 50,
              },
              {
                field: "initiatorBio",
                headerName: initiatorName + "'s Bio",
                flex: 1,
                renderCell: (params: GridRenderCellParams) => <ResponseCell params={params} date={params.row.reportedInititorBio} />,
                cellClassName: (params) => tableCellCss(params.row.initiatorMatch),
              },
              {
                field: "responderPrefernce",
                headerName: responderName + "'s Preferences",
                flex: 1,
                renderCell: (params: GridRenderCellParams) => <ResponseCell params={params} date={params.row.reportedResponderPref} />,
                cellClassName: (params) => tableCellCss(params.row.initiatorMatch),
              },
              {
                field: "responderBio",
                headerName: responderName + "'s Bio",
                flex: 1,
                renderCell: (params: GridRenderCellParams) => <ResponseCell params={params} date={params.row.reportedResponderBio} />,
                cellClassName: (params) => tableCellCss(params.row.responderMatch),
              },
              {
                field: "initiatorPrefernce",
                headerName: initiatorName + "'s Preferences",
                flex: 1,
                renderCell: (params: GridRenderCellParams) => <ResponseCell params={params} date={params.row.reportedInititorPref} />,
                cellClassName: (params) => tableCellCss(params.row.responderMatch),
              },
            ]}
            hideFooter
            autoHeight
            groupingColDef={{
              flex: 1,
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};
